import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Box, CssBaseline, Paper as MuiPaper } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { spacing } from "@mui/system";
import GlobalStyle from "../components/GlobalStyle";
import Navbar from "../components/navbar/Navbar";
import Sidebar from "../components/sidebar/Sidebar";
import Footer from "../components/Footer";
import Settings from "../components/Settings";
import useAuth from "../hooks/useAuth";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { setMenuData } from "../redux/slices/sidebarMenu";
import { WS_URL } from "../config";
import { AvvisiProvider } from "../pages/pages/avvisi/AvvisiContext";
import { ReadDialog } from "../pages/pages/avvisi/ReadDialog";
import { ticketService } from "../pages/pages/supporthub/services/ticketService";
import { setIssueFilters } from "../redux/slices/issueFilters";

const drawerWidth = 258;

const Root = styled.div`
  display: flex;
  min-height: 100vh;
`;

const Drawer = styled.div`
  ${(props) => props.theme.breakpoints.up("md")} {
    width: ${drawerWidth}px;
    flex-shrink: 0;
  }
`;

const AppContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  max-width: 100%;
`;

const Paper = styled(MuiPaper)(spacing);

const MainContent = styled(Paper)`
  flex: 1;
  background: ${(props) => props.theme.palette.background.default};

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    flex: none;
  }

  .MuiPaper-root .MuiPaper-root {
    box-shadow: none;
  }
`;

const DEALER_MENU_ITEM = {
  href: "/pages/dealer",
  title: "Dealer",
  icon: "briefcase",
};

const normalizeTitle = (value = "") =>
  value
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const appendDealerToAttivita = (menu = []) => {
  const visit = (nodes = []) =>
    nodes.map((node) => {
      const cloned = {
        ...node,
        children: Array.isArray(node.children)
          ? visit(node.children)
          : undefined,
      };

      const isAttivitaNode = normalizeTitle(cloned.title) === "attivita";

      if (isAttivitaNode) {
        const children = Array.isArray(cloned.children) ? cloned.children : [];
        const hasDealer = children.some(
          (child) =>
            normalizeTitle(child?.title) === "dealer" ||
            child?.href === DEALER_MENU_ITEM.href,
        );

        if (!hasDealer) {
          cloned.children = [...children, DEALER_MENU_ITEM];
        }
      }

      return cloned;
    });

  return visit(menu);
};

const Dashboard = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { datiMenu, loaded } = useSelector((state) => state.sidebarMenu);
  const { items: notifications } = useSelector((state) => state.notifications);
  const dispatch = useDispatch();
  const { getData } = useAuth();
  const router = useLocation();
  const currentRoute = router.pathname;
  const navigate = useNavigate();

  // Logic for forced popup
  const [activePopup, setActivePopup] = useState(null);

  useEffect(() => {
    // Find the first unread notification that requires a popup
    const popupNotice = notifications.find(
      (n) => n.hasPopup == 1 && n.accettata !== "S",
    );
    if (popupNotice) {
      setActivePopup(popupNotice);
    } else {
      setActivePopup(null);
    }
  }, [notifications]);

  const handleConfirmRead = () => {
    if (activePopup) {
      /* dispatch(markAsReadAsync(activePopup.idUid)); */
      setActivePopup(null);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  useEffect(() => {
    setMobileOpen(false);
  }, [currentRoute]);

  const rawFilters = useSelector((state) => state.issueFilters?.options || []);

  useEffect(() => {
    const getFilters = async () => {
      const token = sessionStorage.getItem("token");

      if (token && rawFilters.length === 0) {
        try {
          const filtersData = await ticketService.getFilters(navigate);
          if (Array.isArray(filtersData)) {
            // Normalize metadata to match component expectations (same logic as useData.js)
            const transformed = filtersData.map((f) => {
              let ds =
                f.type === "list" && f.dataset?.dataset
                  ? f.dataset.dataset
                  : f.dataset;

              const normalizedDs = Array.isArray(ds)
                ? ds.map((item) =>
                    item.value !== undefined
                      ? item
                      : { value: item.id ?? item, label: item.desc ?? item },
                  )
                : ds;

              return { ...f, dataset: normalizedDs };
            });
            dispatch(setIssueFilters(transformed));
          }
        } catch (error) {
          console.error("Error fetching filters:", error);
        }
      }
    };
    getFilters();
  }, [rawFilters.length, dispatch, navigate]);

  useEffect(() => {
    setMobileOpen(false);
    const fetchData = async () => {
      try {
        const token = sessionStorage.getItem("token");
        // Only fetch menu if not already loaded
        if (token && !loaded) {
          // Fetch menu data
          const response = await getData({ action: "menu" });
          const list = appendDealerToAttivita(response.data);
          if (response.status === 0) {
            console.log("Failed to retrive the menu data");
            navigate("/auth/sign-in");
            return;
          }
          dispatch(setMenuData([{ pages: list }]));
        }
      } catch (error) {
        console.log("Error:", error);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem("token")) {
      dispatch({ type: "websocket/connect", payload: WS_URL });
    }

    return () => {
      dispatch({ type: "websocket/disconnect" });
    };
  }, [dispatch]);

  const theme = useTheme();

  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));

  return (
    <Root>
      <AvvisiProvider>
        <CssBaseline />
        <GlobalStyle />
        <Drawer style={{ width: 0 }}>
          <Box sx={{ display: { xs: "none", lg: "none" } }}>
            <Sidebar
              PaperProps={{ style: { width: drawerWidth } }}
              variant="temporary"
              open={mobileOpen}
              onClose={handleDrawerToggle}
              items={datiMenu}
              theme={theme}
            />
          </Box>
        </Drawer>
        <AppContent>
          <Navbar onDrawerToggle={handleDrawerToggle} theme={theme} />
          <MainContent p={isLgUp ? 12 : 5}>
            {children}
            <Outlet />
          </MainContent>
          <Footer />
        </AppContent>
        <Settings />

        {/* Global Forced Popup */}
        {/* <ReadDialog
          open={!!activePopup}
          onClose={handleConfirmRead}
          avviso={activePopup}
          isForced={true}
        /> */}
      </AvvisiProvider>
    </Root>
  );
};

export default Dashboard;
